import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/api/client";
import { hydrateAccountFromToken, useUiStore } from "@/lib/store";

type ErrorBody = {
  error?: string;
  message?: string;
  retryAfterSeconds?: number;
};

const friendlyMessage = (err: any): { message: string; cooldown?: number } => {
  const body = err?.body as ErrorBody | undefined;
  const code = body?.error;
  const retryAfter = body?.retryAfterSeconds;
  switch (code) {
    case "email_undeliverable":
      return {
        message:
          "We can't deliver email to that address. Please contact support or try a different email.",
      };
    case "too_many_requests":
    case "resend_cooldown":
      return {
        message: `Please wait ${retryAfter ?? 30}s before trying again.`,
        cooldown: retryAfter,
      };
    case "email_send_failed":
      return { message: "Couldn't send the email right now. Please try again in a moment." };
    case "invalid_email":
      return { message: "That email address doesn't look valid." };
    default:
      return { message: body?.message || err?.message || "Failed to send verification email." };
  }
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const addNotification = useUiStore((s) => s.addNotification);
  const [searchParams] = useSearchParams();
  const cooldownTimer = useRef<number | null>(null);

  const startCooldown = (seconds: number) => {
    setResendCooldown(seconds);
    if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
    cooldownTimer.current = window.setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
    };
  }, []);

  const requestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await apiClient.post("/v1/auth/signup-link", {
        email,
        fullName: email.split("@")[0],
      });
      setStep("code");
      startCooldown(30);
      addNotification({
        type: "success",
        message: "Check your email for a 6-digit code",
        duration: 5000,
      });
    } catch (err: any) {
      const { message } = friendlyMessage(err);
      addNotification({ type: "error", message });
    } finally {
      setLoading(false);
    }
  };

  const resendLink = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    try {
      await apiClient.post("/v1/auth/resend-link", { email });
      startCooldown(30);
      addNotification({ type: "success", message: "New code sent", duration: 5000 });
    } catch (err: any) {
      const { message, cooldown } = friendlyMessage(err);
      addNotification({ type: "error", message });
      if (cooldown && cooldown > 0) startCooldown(cooldown);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const data = await apiClient.post<{
        accessToken: string;
        refreshToken: string;
      }>("/v1/auth/verify-login-code", { email, code });
      localStorage.setItem("access_token", data.accessToken);
      localStorage.setItem("refresh_token", data.refreshToken);
      hydrateAccountFromToken();
      const next = searchParams.get("next");
      if (next && next.startsWith("/") && !next.startsWith("//")) {
        window.location.href = next;
      } else {
        window.location.href = "/trips";
      }
    } catch (err: any) {
      addNotification({
        type: "error",
        message: err?.message || "Invalid code",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[72vh] items-center justify-center px-4">
      <div className="w-full max-w-md rounded-3xl frost-panel px-6 py-8 md:px-8">
        <div className="mb-8 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] text-ink-light">ROAM ACCESS</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-ink">Welcome back</h1>
          <p className="mt-2 text-sm text-ink-light">Sign in to continue planning</p>
        </div>

        {step === "email" ? (
          <form onSubmit={requestLink} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full rounded-xl border border-white/20 bg-white/[0.08] px-4 py-3.5 text-sm text-ink placeholder:text-ink-light focus:border-accent focus:outline-none"
              autoFocus
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-accent px-4 py-3.5 text-sm font-semibold text-deep transition hover:bg-accent-light disabled:opacity-60"
            >
              {loading ? "Sending..." : "Continue with email"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-4">
            <p className="text-center text-sm text-ink-light">
              Enter the 6-digit code sent to {email}
            </p>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              className="w-full rounded-xl border border-white/20 bg-white/[0.08] px-4 py-3.5 text-center text-2xl tracking-widest text-ink placeholder:text-ink-light focus:border-accent focus:outline-none"
              autoFocus
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-accent px-4 py-3.5 text-sm font-semibold text-deep transition hover:bg-accent-light disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Verify code"}
            </button>
            <button
              type="button"
              onClick={resendLink}
              disabled={resendCooldown > 0 || loading}
              className="w-full text-sm text-ink-light transition hover:text-ink disabled:opacity-60"
            >
              {resendCooldown > 0
                ? `Resend in ${String(Math.floor(resendCooldown / 60)).padStart(2, "0")}:${String(
                    resendCooldown % 60,
                  ).padStart(2, "0")}`
                : "Resend code"}
            </button>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="w-full text-sm text-ink-light transition hover:text-ink"
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
